import {
  RefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import type { ListRenderItem } from "@/components/atoms/FlashList";
import { DragEndParams, RenderItem } from "react-native-draggable-flatlist";

import WatchlistItem from "@/components/organisms/WatchlistItem";
import ManageActionSheetView from "./ManageActionSheetView";

import { CustomPromptRefProps } from "@/components/molecules/CustomPrompt/CustomPromptContainer";
import {
  UserWatchlistRole,
  useWatchlistListMyUserWatchlistsQuery,
  useWatchlistUpdateUserWatchlistPositionMutation,
  WatchlistWatchlistMetaFragment,
} from "@/generated/graphql";
import { MY_WATCHLISTS_PAGE_SIZE } from "@/features/watchlist/constants";
import useCreateWatchlistActions from "@/hooks/useCreateWatchlistActions";
import useMobileScreen from "@/hooks/useMobileScreen";
import useRecordUserGoal, { GoalType } from "@/hooks/useRecordUserGoal";

/**
 *
 */
interface Props {
  show: boolean;
  onClose: () => void;
}

/**
 * Container component that manages the watchlist management actionsheet.
 * Handles watchlist data fetching, reordering, and creation logic.
 * @param root0 - Component props
 * @param root0.show - Whether the actionsheet is currently visible
 * @param root0.onClose - Callback invoked when the actionsheet should be closed
 */
const ManageActionSheetContainer = ({ show, onClose }: Props) => {
  const { data } = useWatchlistListMyUserWatchlistsQuery({
    variables: { first: MY_WATCHLISTS_PAGE_SIZE },
    skip: !show,
  });
  const watchlists = useMemo(
    () =>
      data?.listMyUserWatchlists?.edges
        ?.filter(
          userWatchlist => userWatchlist.node.role === UserWatchlistRole.Owner
        )
        ?.map(edge => edge?.node.watchlist) ?? [],
    [data?.listMyUserWatchlists?.edges]
  );
  const promptRef = useRef<CustomPromptRefProps>(
    null
  ) as RefObject<CustomPromptRefProps>;
  const [reorderedWatchlists, setReorderedWatchlists] = useState(watchlists);
  const isSmallScreen = useMobileScreen();
  const { handleAddWatchlistPress, loading } = useCreateWatchlistActions({
    promptRefForModal: promptRef,
  });
  const recordUserGoal = useRecordUserGoal({
    goalType: GoalType.UpdateWatchlistPosition,
    value: "Update watchlist position",
  });
  const [updateUserWatchlistPosition] =
    useWatchlistUpdateUserWatchlistPositionMutation({
      onCompleted: data =>
        recordUserGoal({
          value: data?.updateUserWatchlistPosition?.id,
          goalType: GoalType.UpdateWatchlistPosition,
        }),
    });

  const handleDragEnd = useCallback(
    (params: DragEndParams<WatchlistWatchlistMetaFragment>) => {
      const updatedItem = params.data[params.to];
      setReorderedWatchlists(params.data);
      updateUserWatchlistPosition({
        variables: {
          input: { watchlistId: updatedItem.id, newPosition: params.to },
        },
        // Background refetch only. The reorder is already applied optimistically
        // via setReorderedWatchlists above, so blocking on the refetch
        // (awaitRefetchQueries) just delayed the settled state for no UX gain.
        refetchQueries: ["WatchlistListMyUserWatchlists"],
      });
    },
    [updateUserWatchlistPosition]
  );

  const handleRenderItem: RenderItem<WatchlistWatchlistMetaFragment> =
    useCallback(
      ({ item, drag, isActive }) => (
        <WatchlistItem
          drag={drag}
          item={item}
          canBeDeleted={watchlists.length > 1}
          isActive={isActive}
          promptRef={promptRef}
        />
      ),
      [watchlists]
    );

  /** No-op drag function for the web FlatList, which has no drag API. */
  const noopDrag = useCallback(() => {}, []);

  /** No-op getIndex function for the web FlatList. */
  const noopGetIndex = useCallback(() => 0, []);

  /**
   * Render adapter for the web FlatList that wraps the draggable renderItem.
   *
   * Memoized because FlatList re-renders every row when `renderItem` changes
   * identity — the reason the View used to hold these three callbacks.
   */
  const renderWebItem: ListRenderItem<WatchlistWatchlistMetaFragment> =
    useCallback(
      ({ item }) =>
        handleRenderItem({
          item,
          drag: noopDrag,
          isActive: false,
          getIndex: noopGetIndex,
        }) as React.ReactElement,
      [handleRenderItem, noopDrag, noopGetIndex]
    );

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- syncing local reorder state to upstream watchlists is the canonical "external-state mirror" effect.
    setReorderedWatchlists(watchlists);
  }, [watchlists]);

  return (
    <ManageActionSheetView
      showActionsheet={show}
      onClose={onClose}
      watchlists={reorderedWatchlists}
      onAddWatchlistPress={handleAddWatchlistPress}
      loading={loading}
      onDragEnd={handleDragEnd}
      positionsUpdating={false}
      renderItem={handleRenderItem}
      promptRef={promptRef}
      isSmallScreen={isSmallScreen}
      renderWebItem={renderWebItem}
    />
  );
};

export default ManageActionSheetContainer;
